/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService, TimeService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { runningStatusE } from 'app/common/constants';
import { type Snapshot } from 'app/common/types/mo';

/**
 * 回滚类型枚举
 */
export enum RestoreType {
    /**
     * 源LUN
     */
    SOURCE_LUN = 0,
    /**
     * 父级快照
     */
    PARENT_SNAPSHOT = 1,
    /**
     * 其他快照
     */
    OTHER_SNAPSHOT = 2
}

@Injectable({ providedIn: 'any' })
export class SnapshotStrService {
    constructor(
        private i18nService: I18nService,
        private time: TimeService,
    ) { }

    // 表格主页面
    table = {
        create: this.get('common.create_button'),
        reconstruct: this.get('protection.snapshot_task_reactive_button'),
        createDuplicateBtn: this.get('protection.snapshot_task_createDuplicate_button'),
        reactive: this.get('common.expandCapacity_button'),
        restore: this.get('common.startRollback_button'),
        delete: this.get('common.delete_button'),
        name: this.get('common.name_column'),
        runningStatus: this.get('common.runningStatus_column'),
        healthStatus: this.get('common.healthStatus_column'),
        mapping: this.get('common.mapping_column'),
        reconstructTitle: this.get('protection.snapshot_task_reactive_tittle'),
        activeTime: this.get('protection.snapshot_term_activatePointTime_column'),
        sourceLUN: this.get('common.sourceLun_label'),
        parentObject: this.get('common.parentObject'),
        cgName: this.get('protection.snapshot_task_detail_owningSnapshotCG_lab'),
        stopRestore: this.get('common.stopRollback_button'),
        modify: this.get('common.modify_button'),
        cancelrollbackTitle: this.get('common.stopRollback_button'),
        cancelrollbackContent: this.cancelRollbackTip,
        commaLab: this.get('common.comma_label'),
        deleteTitle: this.get('protection.snapshot_task_delete_title'),
        deleteContent: this.get('snapshot_task_stopRollbackSnapshot_confirm_msg'),
        startRollbackBtnTips: this.get('protection.snapshotCG_term_starRollbackBtn_tip'),
        multiStartRollbackBtnTips: this.get('protection.snapshotCG_term_starRollbackBtn_batch_size'),
        stopRollbackBtnTips: this.get('protection.snapshotCG_term_stopRollback_tip'),
        multiStopRollbackBtnTips: this.get('protection.snapshotCG_term_stopRollbackBtn_batch_size'),
        stopRollbackTitle: this.get('protection.snapshot_term_stopRollback_title'),
        sourceLunLabel: this.get('storage.smartMigration_term_sourceLunName_column'),
        toTreeViewTip: this.get('protection.snaphost_term_view_switchtoTreeView_tip'),
        toListViewTip: this.get('protection.snaphost_term_view_switchtoListView_tip'),
        noDataLab: this.get('common.result_nodata_tip'),
        activateBtn: this.get('common.active_button'),
        activateBtnDisabledTip: this.get('protection.snapshotCG_term_active_error_tip'),
        activateTitle: this.get('protection.snapshot_task_active_tittle'),
        activateTips: this.get('protection.snapshot_activeSnap_task_confirmation_message'),
        deactiveBtn: this.get('common.inactive_button'),
        deactiveBtnDisabledTip: this.get('protection.snapshotCG_term_cancelActive_error_tip'),
        deactiveTitle: this.get('protection.snapshot_task_deactive_tittle'),
        deactiveTips: this.get('protection.snapshot_deactiveSnap_task_confirmation_message'),
        moreBtn: this.get('common.more_button'),
        vstoreNameLab: this.i18nService.get('vstore_name_lab'),
        vstoreIdLab: this.i18nService.get('vstore_id_lab'),
    };

    // 创建快照
    create = {
        objectTypeLabel: this.get('protection.protection_simplifiedMode_selectObjects_title'),
        protectTypeLabel: this.get('protection.protection_simplifiedMode_selectType_title'),
        selectScheduleLabel: this.get('protection.protection_simplifiedMode_selectSnapPolicy_title'),
        addScheduleLabel: this.get('storage.lun_simplifiedMode_hypercdpSnap_tab'),
        singleSnapLabel: this.get('storage.lun_simplifiedMode_Snap_tab'),
        addScheduleCardTip: this.get('protection.protection_simplifiedMode_snapPolicy_info'),
        singleSnapCardTip: this.get('protection.protection_simplifiedMode_OneOffSnap_info'),
        addNewScheduleBtn: this.get('service.share_simplifiedMode_create_addHypercdp_btn'),

        removeMemberLUN: this.get('rest.removeLun_fromHyperCDPSchedule_msg'),
        advancedSettingTitle: this.get('service.share_simplifiedMode_create_advance_btn'),
        activateCdpScheduleMsg: this.get('rest.startHyperCDPSchedule_msg'),

        createScheduleName: (name: string): string => this.get('rest.create_hyperCDPSchedule_msg', [name]),
        addLunResultName: (lunName, scheduleName): string => this.get('rest.associateLun_toHyperCDPSchedule_msg', [lunName, scheduleName]),
        scheduleLabel: this.get('storage.lun_task_addToHyperCDPScheduley_HyperCDPSchedule_label'),

        vstoreLabel: this.get('common.owningVstore_lab'),
        name: this.get('common.name_column'),
        wwn: this.get('storage.lun_view_summary_localWWN_label'),
        createSnapshotRest: this.get('rest.create_snapshot_msg'),
        createTitle: this.get('protection.protectionGroup_createSnapshot_button'),
        snapshotSuffix: this.get('protection.snapshot_task_create_snapshotSuffix_lab'),
        saveBtnLabel: this.get('common.confirm_lab'),
        okBtnLabel: this.get('common.confirm_button'),
        cancelBtnLabel: this.get('common.cancel_button'),
        placeholderName31: this.rangeByteLengthErr(0, 31),
        placeholderName27: this.rangeByteLengthErr(0, 27),
        placeholderName251: this.rangeByteLengthErr(0, 251),
        placeholderName255: this.rangeByteLengthErr(0, 255),
        des: this.get('common.description_column'),
        placeholderDes: this.rangeByteLengthErr(0, 255),
        avaiableLunLab: this.get('protection.snapshot_task_create_avaiableLun_lab'),
        selectedLab: this.get('protection.snapshot_task_create_selectedLUN_lab'),
        capacity: this.get('common.capacity_column'),
        notEnoughErr: this.get('settings.userAndRole_roleManagement_task_add_noSelectObject_tip'),
        filterDataTip: this.get('common.filterNoMeetData_tip'),
        sourceLUN: this.table.sourceLUN,
        createSnapshotTitle: (params): string => this.get('rest.create_snapshot_msg', [params]),
        createModeLab: this.get('service.share_term_createMode_lab'),
        newCreateSnapshotLab: this.get('protection.fs_hyperClone_task_create_hyperSnapTpye_createhyperSnap_value'),
        selectTargetLunLab: this.get('protection.snapshot_task_create_Mode_targetLUN_value'),
        targetLunLabel: this.get('common.targetLun_label'),
        targetLunErrorTip: this.get('protection.snapshot_task_create_Mode_targetLUN_err_tip'),
        targetLunSnapshotTip: this.get('protection.snapshot_task_create_targetLUN_tip'),
        targetLunIdLab: this.get('common.ID_column'),
        createSnapshotPromptTitle: this.get('protection.protectionGroup_createSnapshot_button'),
        createSnapshotPromptMsg: this.get('protection.snapshot_task_create_confirm_msg'),
        vstoreNameLab: this.i18nService.get('vstore_name_lab'),
        vstoreIdLab: this.i18nService.get('vstore_id_lab'),
    };

    // 详情
    detail = {
        cancelBtnLabel: this.get('common.close_button'),
        HEALTHSTATUS: this.table.healthStatus,
        RUNNINGSTATUS: this.table.runningStatus,
        activeTime: this.table.activeTime,
        WWN: this.get('common.device_WWN_label'),
        NGUID: this.get('storage.LUN_task_view_NGUID_lab'),
        Des: this.create.des,
        sourceLUN: this.table.sourceLUN,
        parentObjName: this.table.parentObject,
        cascadingLevel: this.get('protection.snapshot_task_detail_cascadingLevel_lab'),
        mapping: this.get('common.mapping_column'),
        owningLunGroup: this.get('protection.lun_view_summary_belongedLunGroup_tab'),
        owningSnapshotCG: this.get('protection.snapshot_task_detail_owningSnapshotCG_lab'),
        restoreObjName: this.get('protection.snapshot_task_detail_rollbackObjectName_lab'),
        restoreSpeed: this.get('protection.snapshot_task_detail_rollbackSpeed_lab'),
        restoreStartTime: this.get('protection.snapshot_task_detail_startTimeOfRollback_lab'),
        restoreEndTime: this.get('protection.snapshot_task_detail_endTimeOfRollback_lab'),
        operationBtn: this.get('common.operation_button'),
        vstoreName: this.i18nService.get('vstore_name_lab'),
    };

    modify = {
        modifyTitle: this.get('protection.protectionGroup_modifySnapshot_title'),
        saveBtnLabel: this.get('common.confirm_lab'),
        okBtnLabel: this.get('common.confirm_button'),
        cancelBtnLabel: this.get('common.cancel_button'),
        des: this.create.des,
        placeholderDes: this.rangeByteLengthErr(0, 255),
        name: this.table.name,
        snapshotSuffix: this.create.snapshotSuffix,
        speed: this.detail.restoreSpeed,
        rollbackSpeedTip: this.rollbackSpeedTip(),
        modifySnapshotTitle: this.get('protection.protectionGroup_modifySnapshot_title'),
    };

    restore = {
        restoreTitle: this.table.restore,
        restoreWinTitle: this.get('protection.snapshot_term_startRollback_title'),
        stopRestoreTitle: this.get('protection.snapshot_term_stopRollback_title'),
        snapshot: this.get('common.snapshot_title'),
        restoreObj: this.get('protection.snapshot_task_startRollback_rollbackObject_lab'),
        saveBtnLabel: this.get('common.confirm_lab'),
        okBtnLabel: this.get('common.confirm_button'),
        restore: this.get('protection.snapshot_term_startRollback_button'),
        cancelBtnLabel: this.modify.cancelBtnLabel,
        restoreTo: this.get('common.rollbackTo'),
        restoreSpeed: this.modify.speed,
        restoreContent: this.rollbackTip,
        rollbackSpeedTip: this.rollbackSpeedTip(),
        selectBtn: this.get('storage.storagePool_task_create_requiredDisk_select_button'),
        expandBtn: this.get('common.expand_label'),
    };

    delete = {
        winTitle: this.get('protection.snapshot_task_delete_title'),
        deleteTip: this.get('protection.snapshot_task_deleteSnap_confirm_msg'),
        deleteWithTargetLunTip: this.get('protection.snapshot_task_delete_confirm_msg'),
        deleteTargetLunLab: this.get('protection.clone_term_deleteTargerLUN_column'),

        nameLab: this.table.name,
        runningStatusLab: this.table.runningStatus,
        activedAtLab: this.table.activeTime,
    };

    reconstruct = {
        nameLab: this.table.name,
        runningStatusLab: this.table.runningStatus,
        activedAtLab: this.table.activeTime,

        winTitle: this.get('protection.snapshot_task_reactive_tittle'),
        reconstructTitle: this.get('protection.snapshot_task_reactive_tittle'),
        typeLabel: this.get('protection.snapshot_task_reactive_sourceObjectType_label'),
        parentObjectLabel: this.get('protection.snapshot_task_reactive_parentObject_checkbox'),
        hyperCdpLabel: this.get('protection.HyperCDP_Object_checkbox'),
        nameLabel: this.get('common.name_column'),
        activateAtCol: this.get('protection.snapshot_term_activatePointTime_column'),
        runningStatusLabel: this.get('common.runningStatus_column'),
        sourceLUN: this.get('common.sourceLun_label'),
        sourceLunLabel: this.table.sourceLunLabel,
        hyperCdpLab: this.get('protection.HyperCDP_title'),
        notEnoughErr: this.get('common.selectOnlyOneObject_lab'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        commaLab: this.table.commaLab,
    };

    // 健康状态
    HEALTHSTATUS = {
        1: this.get('common.EnumDefine_STATUS_E_NORMAL'),
        2: this.get('common.EnumDefine_HEALTH_STATUS_fault'),
        15: this.get('common.EnumDefine_HEALTH_STATUS_E_WRITE_PROTECT'),
    };

    // 回滚速率选项列表
    rollbackSpeedOptions: SelectOption[] = [
        {
            label: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_LOW'),
            value: '1',
        },
        {
            label: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_MIDDLE'),
            value: '2',
        },
        {
            label: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_HIGH'),
            value: '3',
        },
        {
            label: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_ASAP'),
            value: '4',
        },
    ];

    getCascadingLevelOptions: SelectOption[] = [
        {
            label: '0',
            value: '0',
        },
        {
            label: '1',
            value: '1',
        },
        {
            label: '2',
            value: '2',
        },
        {
            label: '3',
            value: '3',
        },
        {
            label: '4',
            value: '4',
        },
        {
            label: '5',
            value: '5',
        },
        {
            label: '6',
            value: '6',
        },
        {
            label: '7',
            value: '7',
        },
    ];

    restoreObjTypeOpts: SelectOption[] = [
        {
            label: this.get('common.sourceLun_label'),
            value: RestoreType.SOURCE_LUN,
        },
        {
            label: this.get('protection.snapshotCG_task_rollbackObject_fatherSnapshot_radio'),
            value: RestoreType.PARENT_SNAPSHOT,
        },
        {
            label: this.get('protection.snapshotCG_task_rollbackObject_otherSnapshot_radio'),
            value: RestoreType.OTHER_SNAPSHOT,
        },
    ];

    /**
     * 重建提示
     * @param data
     */
    reconstructTip(isParentObject?: boolean): string {
        if (isParentObject) {
            return this.get('protection.snapshot_term_sourceLUN_rebuildSnapshot_msg');
        }
        return this.get('protection.snapshot_term_HyperCDP_rebuildSnapshot_msg');
    }

    /**
     * 重建结果
     * @param name
     */
    reconstructResult(name: string): string {
        return this.get('rest.reactiveSnapshot_msg', [name + ' ']);
    }

    /**
     * 回滚速率提示
     * @param name
     */
    rollbackSpeedTip(): string {
        return this.get('common.speed_valid_label');
    }

    /**
     * 获取运行状态选项列表
     * @param names
     */
    getRunningStatusOptions(): any {
        const names = ['Activated', 'Restore', 'Inactive'];
        return names.reduce((obj: any, name: string) => {
            obj[runningStatusE[name].value] = this.get(runningStatusE[name].i18n);
            return obj;
        }, {});
    }

    /**
     * 获取运行状态国际化
     * @param names
     */
    getAllRunningStatusOptions(): string {
        const names = ['Unknown', 'Activated', 'Restore', 'Inactive', 'Initializing', 'Deleting'];
        return names.reduce((obj: any, name: string) => {
            obj[runningStatusE[name].value] = this.get(runningStatusE[name].i18n);
            return obj;
        }, {});
    }

    /**
     * 必填错误
     * @param name
     */
    requiredErr(name: string): string {
        return this.get('common.null_validation', [name]);
    }

    /**
     * 长度范围错误
     * @param min
     * @param max
     */
    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    /**
     * 名称格式错误
     * @param name
     */
    commonNameErr(name: string): string {
        return this.get('common.nameformat_validation', [name]);
    }

    /**
     * 删除结果
     * @param name
     */
    deleteResult(name: string): string {
        return this.get('rest.delete_snapshot_msg', [name]);
    }

    /**
     * 回滚提示
     * @param data
     */
    rollbackTip(data: Snapshot, targetName: string): string {
        return this.get('protection.snapshot_task_rollback_withHighSpeed_confirm_msg',
            [data.NAME, targetName, this.renderTime(data.TIMESTAMP)]);
    }

    /**
     * 取消回滚提示
     * @param data
     */
    cancelRollbackTip(data: Snapshot): string {
        return this.get('protection.snapshot_task_stopRollbackSnapshot_confirm_msg',
            [data.NAME, data.ROLLBACKTARGETOBJNAME, this.renderTime(data.TIMESTAMP)]);
    }

    /**
     * 扩容快照提示
     * @param snapshotName
     * @param capacity
     */
    expanSnapshotTip(snapshotName: string, capacity: string): string {
        return this.get('protection.snapExpand_confirm_msg', [snapshotName, capacity]);
    }

    /**
     * 扩容LUN提示
     * @param lunName
     * @param capacity
     */
    expandLunTip(lunName: string, capacity: string): string {
        return this.get('storage.lun_task_expand_confirm_msg', [lunName, capacity]);
    }

    // 扩容描述
    expansionContent(capacity: string, LunCapacity: string): string {
        return this.get('protection.snapshot_task_snapshotAndrollbackObejctOfCapacity_diff_tip', [capacity, LunCapacity]);
    }

    // 修改速率为高或最高时的tips
    modifySpeed(): string {
        return this.get('protection.snapshotRollback_changeToHighSpeed_confirm_msg');
    }

    // 修改名称和描述提示
    modifyNameOrDescription(name: string): string {
        return this.get('protection.snapshot_changeSnapName_task_confirmation_message', [name]);
    }

    renderData(dataArr: Snapshot[]): Snapshot[] {
        dataArr.map((data) => {
            if(!isNaN(Number(data.HEALTHSTATUS))) {
                data.HEALTHSTATUS = this.HEALTHSTATUS[data.HEALTHSTATUS];
            }
            if(!isNaN(Number(data.RUNNINGSTATUS))) {
                data.RUNNINGSTATUS = this.getAllRunningStatusOptions()[data.RUNNINGSTATUS];
            }
        });
        return dataArr;
    }

    renderTime(time: string): string {
        return (isNaN(Number(time)) || Number(time) <= 0) ?
            '--' :
            this.time.formatUTC(Number(time) * 1e3);
    }

    get(key: string, params?: any[] | boolean, colon?: boolean): string {
        return this.i18nService.get(key, params, colon);
    }

    activeTip(name: string): string {
        return this.get('rest.activeSnapshot_msg', [name]);
    }

    deactiveTip(name: string): string {
        return this.get('rest.deactiveSnapshot_msg', [name]);
    }
}

